// Single shared notifications source for the whole authenticated app (S48 follow-up).
// A ONE provider mounted under AuthProvider runs the fetch + 45s poll (+ focus refetch); every
// consumer (the global header bell AND the ops-board own-booking interrupt) reads the SAME state
// via useNotifications() — so there is exactly one poll, never a second fetch per surface.
//
// Online-only: a failed fetch leaves the last state untouched. markRead/dismiss call the server
// then refresh so the unread count + the board interrupt stay accurate.

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { fetchNotifications, markRead as apiMarkRead, dismissNotice as apiDismiss, type AppNotification } from '../lib/conflicts';
import { useAuth } from './AuthContext';

const POLL_MS = 45_000; // mirrors the board's original notification cadence

export interface NotificationsValue {
  notifications: AppNotification[];
  unread: number;
  markRead: (id: string) => Promise<void>;
  dismiss: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const NotificationsCtx = createContext<NotificationsValue | null>(null);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(async () => {
    const n = await fetchNotifications();
    if (n) { setNotifications(n.items); setUnread(n.unread); }
  }, []);

  // Keep the polling effect stable (depends only on auth) while always calling the latest refresh.
  const refreshRef = useRef(refresh);
  useEffect(() => { refreshRef.current = refresh; }, [refresh]);

  useEffect(() => {
    if (!isAuthenticated) { setNotifications([]); setUnread(0); return; }
    void refreshRef.current();
    const iv = setInterval(() => void refreshRef.current(), POLL_MS);
    const onFocus = () => void refreshRef.current();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(iv); window.removeEventListener('focus', onFocus); };
  }, [isAuthenticated]);

  const markRead = useCallback(async (id: string) => { await apiMarkRead(id); await refreshRef.current(); }, []);
  const dismiss = useCallback(async (id: string) => { await apiDismiss(id); await refreshRef.current(); }, []);

  return (
    <NotificationsCtx.Provider value={{ notifications, unread, markRead, dismiss, refresh }}>
      {children}
    </NotificationsCtx.Provider>
  );
}

export function useNotifications(): NotificationsValue {
  const v = useContext(NotificationsCtx);
  if (!v) throw new Error('useNotifications must be used within a NotificationsProvider');
  return v;
}
